import {
	useDeleteDailyMaxDepositMutation,
	useGetDailyMaxDepositQuery,
	useSetDailyMaxDepositMutation,
} from "@api/accountManagement";
import { Form, message } from "antd";
import { useMemo, useState } from "react";

const AMOUNT_REGEX = /^[0-9]+(\.[0-9]{1,2})?$/;

const useController = () => {
	const [form] = Form.useForm();
	const [isButtonDisabled, setIsButtonDisabled] = useState(true);

	const { data: depositLimitData } = useGetDailyMaxDepositQuery();

	const [setDailyMaxDeposit, { isLoading: isSetting }] = useSetDailyMaxDepositMutation();
	const [deleteDailyMaxDeposit, { isLoading: isDeleting }] = useDeleteDailyMaxDepositMutation();

	const state = useMemo(() => {
		const amount = depositLimitData?.daily_max_deposit || 0;
		const startDate = depositLimitData?.createdAt || 0;
		const isSet = amount > 0;

		return {
			isDepositLimitSet: isSet,
			depositLimitAmount: amount,
			depositLimitStartDate: startDate,
			isSetting,
			isDeleting,
			isButtonDisabled,
		};
	}, [depositLimitData, isSetting, isDeleting, isButtonDisabled]);

	const handlers = useMemo(
		() => ({
			handleDepositLimit: async (values) => {
				try {
					await setDailyMaxDeposit({
						maxDailyDeposit: parseFloat(values.depositLimitAmount),
					}).unwrap();

					message.success("Daily deposit limit set successfully");
					form.resetFields();
					setIsButtonDisabled(true);
				} catch (error) {
					const errorMessage =
						error?.data?.message || error?.message || "Failed to set deposit limit";
					message.error(errorMessage);
					console.error("Failed to set deposit limit:", error);
				}
			},

			handleFormValuesChange: (changedValues) => {
				setIsButtonDisabled(!changedValues.depositLimitAmount);
			},

			handleDeleteDepositLimit: async () => {
				try {
					await deleteDailyMaxDeposit().unwrap();
					message.success("Daily deposit limit removed successfully");
				} catch (error) {
					const errorMessage =
						error?.data?.message || error?.message || "Failed to remove deposit limit";
					message.error(errorMessage);
					console.error("Failed to remove deposit limit:", error);
				}
			},
		}),
		[setDailyMaxDeposit, deleteDailyMaxDeposit, form]
	);

	// Helpers
	const helpers = useMemo(
		() => ({
			AMOUNT_REGEX,
			form,
		}),
		[form]
	);

	return {
		state,
		handlers,
		helpers,
	};
};

export default useController;
